import { Dispatch, FunctionComponent, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faBan, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import { classNames } from "@/utils/classNames";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Icon } from "@/components";

import { ListPerson } from "../../../List/schema/list";
import { renderCurrentScoreResult } from "../../../List/utils/allColumns";
import { LeadName } from "../../../List/components/CampaignOverview/components/Lead/components/LeadName";
import { JobTitle } from "../../../List/components/CampaignOverview/components/Lead/components/JobTitle";
import { OrgName } from "../../../List/components/CampaignOverview/components/Lead/components/OrgName";
import { LeadColorDots } from "../../../List/components/CampaignOverview/components/Lead/components/LeadColorDots";
import { LeadColorLabels } from "../../../List/components/CampaignOverview/components/Lead/components/LeadColorLabels";
import { OutboxSenderInfo } from "../../../List/components/CampaignOverview/components/Lead/components/OutboxSenderInfo";
import { DeepResearchStatusChip } from "../../../List/components/CampaignOverview/components/Lead/components/DeepResearchStatusChip";
import { useDeepResearchStatus } from "../../../List/components/CampaignOverview/components/Lead/utils/useDeepResearchStatus";
import { ProspectSentimentDropdown } from "../../../List/components/CampaignOverview/components/Lead/components/ProspectSentimentDropdown";
import { ReplyChannelBadge } from "../../../List/components/CampaignOverview/components/Lead/components/ReplyChannelBadge";
import { useSwipeToDone } from "../../../List/components/CampaignOverview/components/Lead/utils/useSwipeToDone";
import { convertHtmlToLinkedInText } from "../../../List/components/CampaignOverview/components/Lead/utils/convertHtmlToLinkedin.util";
import {
	calendarInviteTitle,
	isCalendarInvite,
} from "../../../List/components/CampaignOverview/utils/resolveCalendarInviteDisplay.util";
import { LeadCardCompact } from "./LeadCardCompact";
import {
	getCardProvenance,
	getLastInboundResponse,
	getLeadScore,
	getPreviewText,
	getSenderFromConversations,
	resolveCardCampaignConfig,
} from "./lead-card-compact.helper";

interface InboxLeadCompactProps {
	row: ListPerson;
	handleSetCurrentLead: (index: number) => void;
	allowCampaignChange?: boolean;
	createBlacklistIsLoading?: boolean;
	bulkChangeStatusIsLoading?: boolean;
	setShowBlacklistModal?: Dispatch<SetStateAction<boolean>>;
	setShowDoneModal?: Dispatch<SetStateAction<boolean>>;
	setSelected?: (selected: number[]) => void;
	selected?: number[];
	/** Collapsed lead-card column: show only the score rail, hide the details. */
	isCollapsed?: boolean;
}

/**
 * Compact Inbox lead card (PREC-3850): mockup layout (identity column, provenance
 * line, sentiment chip, reply preview) while keeping the functional behaviors of
 * the legacy card — swipe-to-done, hover quick actions, sentiment dropdown,
 * bulk-selection checkbox, CRM/research chips.
 */
export const InboxLeadCompact: FunctionComponent<InboxLeadCompactProps> = ({
	row,
	handleSetCurrentLead,
	allowCampaignChange = false,
	setSelected,
	selected,
	createBlacklistIsLoading,
	bulkChangeStatusIsLoading,
	setShowBlacklistModal,
	setShowDoneModal,
	isCollapsed = false,
}) => {
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);
	const { t } = useTranslation();

	const rowIndex = inbox.indexOf(row);

	const lastResponse = useMemo(() => getLastInboundResponse(row), [row]);
	// PREC-3902: the mail carrying a meeting invitation is Outlook's join-link boilerplate, which
	// previews as an empty line or a stray "Microsoft Teams-Besprechung" — the event's own title is
	// what the card has to show.
	const inviteTitle = useMemo(
		() =>
			lastResponse && isCalendarInvite(lastResponse)
				? t("inbox.calendarInvite.preview", { title: calendarInviteTitle(lastResponse) })
				: null,
		[lastResponse, t]
	);
	const previewText = lastResponse?.message ? (
		<span className="italic">
			{getPreviewText(convertHtmlToLinkedInText(lastResponse.message))}
		</span>
	) : undefined;
	const fallbackSender = useMemo(() => getSenderFromConversations(row), [row]);
	const provenance = getCardProvenance(row);
	const deepResearchStatus = useDeepResearchStatus(row);
	const campaignConfig = useMemo(() => resolveCardCampaignConfig(row), [row]);

	const { dragX, isDragging, bind, shouldIgnoreClick } = useSwipeToDone({
		disabled: isCollapsed || !setShowDoneModal,
		onCommit: () => {
			handleSetCurrentLead(rowIndex);
			setShowDoneModal?.(true);
		},
	});

	const score = getLeadScore(row);

	const scoreBadge = renderCurrentScoreResult({
		scoring: row.prospect?.scorePersonaCategoryResult,
		score,
	});

	if (isCollapsed) {
		return (
			<div className="flex flex-col items-center gap-1 py-2 w-full border-b border-border-light">
				{scoreBadge}
			</div>
		);
	}

	return (
		<div className="relative group/leadcard z-0 hover:z-50">
			{setShowDoneModal && dragX < 0 && (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-end gap-2 rounded-lg bg-status-success pr-5 text-sm font-semibold text-white">
					<Icon className="h-4 w-4" icon={faCheckCircle} />
					Done
				</div>
			)}
			<div
				className={classNames(isDragging ? "" : "transition-transform duration-200")}
				style={dragX ? { transform: `translateX(${dragX}px)` } : undefined}
				{...bind}
			>
				<LeadCardCompact
					checkbox={
						allowCampaignChange && campaignConfig ? (
							<Checkbox
								isChecked={selected?.includes(row.id) || false}
								onChange={() => {
									if (selected?.includes(row.id)) {
										setSelected?.([...(selected || []).filter((id) => id !== row.id)]);
									} else {
										setSelected?.([...(selected || []), row.id]);
									}
								}}
							/>
						) : undefined
					}
					chips={
						<>
							{row.prospect?.sentiment && lastResponse && (
								<ProspectSentimentDropdown conversation={lastResponse} />
							)}
							{deepResearchStatus && <DeepResearchStatusChip compact status={deepResearchStatus} />}
						</>
					}
					company={<OrgName compact row={row} />}
					dimmed={row.schedules?.[0]?.active === false}
					gutter={
						<>
							{scoreBadge}
							{lastResponse && <ReplyChannelBadge lastResponse={lastResponse} />}
							<OutboxSenderInfo hideWhenUnknown fallbackSender={fallbackSender} row={row} />
							<LeadColorDots row={row} onReviewChat={() => handleSetCurrentLead(rowIndex)} />
						</>
					}
					isActive={currentLeadIndex === rowIndex}
					name={<LeadName row={row} />}
					preview={inviteTitle ? <span className="italic">{inviteTitle}</span> : previewText}
					provenance={{
						listName: provenance.listName,
						listTo: provenance.listTo,
						campaignLabel: provenance.campaignName ?? t("outbox.card.chatOutreach"),
					}}
					time={lastResponse?.sentAt ? format(new Date(lastResponse.sentAt), "HH:mm") : undefined}
					title={<JobTitle compact row={row} />}
					topLabels={<LeadColorLabels row={row} />}
					onClick={() => {
						if (shouldIgnoreClick()) {
							return;
						}

						handleSetCurrentLead(rowIndex);
					}}
				/>
			</div>
			{(setShowBlacklistModal || setShowDoneModal) && (
				<div className="hidden group-hover/leadcard:flex absolute left-0 right-0 top-full z-50 flex-row gap-2 p-2 bg-white border border-border-light rounded-b-xl shadow-md">
					{setShowBlacklistModal && (
						<button
							className="flex-1 cursor-pointer flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-status-error/40 text-status-error font-semibold text-sm hover:bg-status-error-light"
							disabled={createBlacklistIsLoading}
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setShowBlacklistModal(true);
							}}
						>
							<Icon
								className="w-3.5 h-3.5"
								icon={createBlacklistIsLoading ? faSpinner : faBan}
								spin={createBlacklistIsLoading}
							/>
							Unsubscribe
						</button>
					)}
					{setShowDoneModal && (
						<button
							className="flex-1 cursor-pointer flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-status-success text-white font-semibold text-sm hover:bg-status-success/90"
							disabled={bulkChangeStatusIsLoading}
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setShowDoneModal(true);
							}}
						>
							<Icon
								className="w-3.5 h-3.5"
								icon={bulkChangeStatusIsLoading ? faSpinner : faCheckCircle}
								spin={bulkChangeStatusIsLoading}
							/>
							Mark as done
						</button>
					)}
				</div>
			)}
		</div>
	);
};
